import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import type { TicketTrigger } from "@/constants/tickets";
import { convertTimeFrameToUnix, convertTimeFrameToUTCUnix } from "@/_helpers/date";
import { downloadFile } from "@/_helpers/download";
import type { Paged, TimeFrame } from "@/types";
import type {
  SubscriptionModule,
  TicketGeneratingSubscriptionModule,
} from "@/constants/workplaces";
import moment from "moment/moment";
import type { OsType } from "@/constants/devices.ts";
import {
  DnsDomainStatus,
  DNS_REPORT_DEFAULT_PAGE_SIZE,
  MSP_WARNING_EMAIL_EVENTS_WORKSPACE_LIMIT,
  ReportType,
  WARNING_EMAIL_EVENTS_PAGE_SIZE,
} from "@/constants/reports.ts";
import type { Pagination } from "@/types";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import { StoreModule } from "@/constants/store.ts";
import sortBy from "lodash/sortBy";

export interface DnsReportDomain {
  id?: string;
  devices: number;
  domain: string;
  hits: number;
  status: DnsDomainStatus;
  details: {
    user: string | null;
    model: string;
    hits: number;
  }[];
}

export type TrendPoint = { date: number; value: number };

/** Per-module numbers keyed by module, absent for a module the workspace is not subscribed to. */
export type ExecutiveSummaryModuleNumbers = Partial<
  Record<TicketGeneratingSubscriptionModule, number>
>;

/** One dated row of the users-and-devices trend. The backend sends both series in one row. */
export interface ExecutiveSummaryUsersAndDevicesTrendPoint {
  date: number;
  users: number;
  devices: number;
}

export interface ExecutiveSummaryUsersAndDevicesTrend {
  items: ExecutiveSummaryUsersAndDevicesTrendPoint[];
  usersDelta: number;
  devicesDelta: number;
}

/** One dated row of the ticket trend, carrying a count per subscribed module alongside the date. */
export interface ExecutiveSummaryTicketsTrendPoint extends ExecutiveSummaryModuleNumbers {
  date: number;
}

export interface ExecutiveSummaryTicketsTrend {
  items: ExecutiveSummaryTicketsTrendPoint[];
  deltas: ExecutiveSummaryModuleNumbers;
}

export interface ExecutiveSummaryTrends {
  usersAndDevices: ExecutiveSummaryUsersAndDevicesTrend;
  tickets: ExecutiveSummaryTicketsTrend;
}

export type CloudSecurityImpact = { threats: number };
export type EndpointSecurityImpact = {
  risks: number;
  autoResolvedPercent: number;
};
export type EmailSecurityImpact = {
  spamBlocked: number;
  additionalThreats: number;
  autoResolvedPercent: number;
};
export type DataGovernanceImpact = {
  tickets: number;
  healthData: number;
  creditCardData: number;
};
export type EdrImpact = { indicators: number };

export type ExecutiveSummaryImpactByModule = {
  [SubscriptionModule.CLOUD_SECURITY]: CloudSecurityImpact;
  [SubscriptionModule.ENDPOINT_SECURITY]: EndpointSecurityImpact;
  [SubscriptionModule.EMAIL_SECURITY]: EmailSecurityImpact;
  [SubscriptionModule.USER_DATA_GOVERNANCE]: DataGovernanceImpact;
  [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: DataGovernanceImpact;
  [SubscriptionModule.EDR]: EdrImpact;
};

export type ExecutiveSummaryModuleImpact =
  ExecutiveSummaryImpactByModule[TicketGeneratingSubscriptionModule];

export type ExecutiveSummarySecurityImpact = Partial<ExecutiveSummaryImpactByModule>;

export type ExecutiveSummaryEmailListCounts = {
  emails: number;
  domains: number;
  ips: number;
};

export type ExecutiveSummaryBotAttacks = {
  total: number;
  byCountry: { country: string; count: number }[];
};

export type ProtectionModuleSection = {
  totalVulnerabilities: number;
  autoResolvedPercent: number;
  vulnerabilities: { [key in TicketTrigger]: number };
  violators: { [displayValue: string]: number };
};

/** Email Security carries everything a protection section does, plus its own extras. */
export interface EmailSecurityWarningOnly {
  totalEvents: number;
  percentOfInboundEmail: number;
}

export interface EmailSecurityProtectionSection extends ProtectionModuleSection {
  remainingTicketsTotal: number;
  allowlist: ExecutiveSummaryEmailListCounts;
  blocklist: ExecutiveSummaryEmailListCounts;
  warningOnly: EmailSecurityWarningOnly;
}

export type ExecutiveSummaryReportProtectionSettings = Partial<{
  [SubscriptionModule.CLOUD_SECURITY]: ProtectionModuleSection & {
    botAttacks: ExecutiveSummaryBotAttacks;
  };
  [SubscriptionModule.ENDPOINT_SECURITY]: ProtectionModuleSection;
  [SubscriptionModule.EMAIL_SECURITY]: EmailSecurityProtectionSection;
  [SubscriptionModule.USER_DATA_GOVERNANCE]: ProtectionModuleSection;
  [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: ProtectionModuleSection;
  [SubscriptionModule.EDR]: ProtectionModuleSection;
}>;

/** A completed-out-of-total pair, rendered as a completion ring. */
export interface ExecutiveSummaryCompletion {
  completed: number;
  total: number;
}

export type ExecutiveSummarySecurityAwarenessReportData = {
  emailsSent: number;
  trainingsSent: number;
  securityTrainings: ExecutiveSummaryCompletion;
  phishingTrainings: ExecutiveSummaryCompletion;
  topUsersFailedPhishing: SecurityAwarenessUserFailureRecord[];
  topUsersFailedTraining: SecurityAwarenessUserFailureRecord[];
};

export interface ExecutiveSummaryUsersSummary {
  total: number;
  protected: number;
  unProtected: number;
}

export interface ExecutiveSummaryDevicesSummary {
  total: number;
  totalOffline?: number;
  types: Record<OsType, number>;
  outdatedDevices?: Record<OsType, number>;
}

export interface ExecutiveSummaryTicketsSummary {
  total: number;
  totalUnProcessed: number;
  /** Sent by the backend, 0–100. Absent (or `null`) when there is nothing to measure. */
  autoResolvedPercent?: number;
}

export interface ExecutiveSummaryReportData {
  users: ExecutiveSummaryUsersSummary;
  devices: ExecutiveSummaryDevicesSummary;
  tickets: ExecutiveSummaryTicketsSummary;
  protection: ExecutiveSummaryReportProtectionSettings;
  trends: ExecutiveSummaryTrends;
  securityImpact: ExecutiveSummarySecurityImpact;
  [SubscriptionModule.SECURITY_AWARENESS_TRAINING]: ExecutiveSummarySecurityAwarenessReportData;
}

export interface DnsSummaryReportData {
  totalQueries: number;
  totalBlockedQueries: number;
  generalChartData: Array<DnsReportChartData>;
  blockedChartData: Array<DnsReportChartData>;
  topDomainsPermitted: Array<DomainData>;
  topDomainsBlocked: Array<DomainData>;
  topClients: Array<HostData>;
  topClientsBlocked: Array<HostData>;
  domainsList: Paged<DnsReportDomain>;
  domainsPagination: Pagination;
  domainsSorting: string;
  dnsDomainsDevices: string[];
  dnsDomainsUsers: string[];
}

export interface DnsReportChartData {
  x: string;
  y: number;
  startDate: number;
  endDate: number;
}

interface DomainData {
  domain: string;
  count: number;
}
interface HostData {
  host: string;
  count: number;
}

export interface SecurityAwarenessUserFailureRecord {
  userId: string;
  offenses: number;
}

export interface PhishingSimulationSummary {
  failed: number;
  noAction: number;
  reported: number;
}

export interface SecurityAwarenessPhishingSimulationDetails {
  emailName: string;
  receivedDate: number;
  failed: boolean;
  noAction: boolean;
  reported: boolean;
  associatedTrainingCompleted: boolean;
}

export type ManagedServicesResolvedTickets = {
  [moduleName in SubscriptionModule]: {
    total: number;
    vulnerabilities: {
      [key in TicketTrigger]: number;
    };
  };
};

export interface ManagedServicesReportData {
  totalResolvedTickets: number;
  resolvedTicketsByModules: ManagedServicesResolvedTickets;
}
export interface SecuredMessagesReportData {
  total: number;
  protectedUsers: {
    [email: string]: number;
  };
}

export interface WarningOnlyEmailEventsWarningsByType {
  newlyRegisteredDomain: number;
  encryptedAttachment: number;
  unverifiedSender: number;
  unknownExternalSender: number;
}

export interface WarningOnlyEmailEventsUserRecord {
  email: string;
  total: number;
  trend: number;
  newlyRegisteredDomain: number;
  encryptedAttachment: number;
  unverifiedSender: number;
  unknownExternalSender: number;
}

export interface WarningOnlyEmailEventsUserDetailsPage {
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface WarningOnlyEmailEventsReportData {
  totalEvents: number;
  percentOfInboundEmail: number;
  warningsByType: WarningOnlyEmailEventsWarningsByType;
  topAffectedUsers: { email: string; total: number }[];
  userDetails: WarningOnlyEmailEventsUserRecord[];
  userDetailsPage: WarningOnlyEmailEventsUserDetailsPage;
}

export interface WarningOnlyEmailEventsWorkspaceRecord {
  workspaceId: string;
  displayName: string;
  total: number;
  trend: number;
  newlyRegisteredDomain: number;
  encryptedAttachment: number;
  unverifiedSender: number;
  unknownExternalSender: number;
}

export interface WarningOnlyEmailEventsMspReportData {
  totalEvents: number;
  percentOfInboundEmail: number;
  warningsByType: WarningOnlyEmailEventsWarningsByType;
  topAffectedWorkspaces: { displayName: string; total: number }[];
  workspaceDetails: WarningOnlyEmailEventsWorkspaceRecord[];
}

export interface SecurityAwarenessSummary {
  overDue: number;
  toDo: number;
  completed: number;
}

export interface SecurityAwarenessTrainingEngagementUser {
  userId: string;
  offenses: number;
}

export interface SecurityAwarenessTrainingEngagementTraining {
  courseName: string;
  percentage: number;
}

export interface SecurityAwarenessTrainingEngagement {
  overdueCourses: SecurityAwarenessTrainingEngagementUser[];
  trainingStatus: SecurityAwarenessTrainingEngagementTraining[];
}

export interface SecurityAwarenessTrainingTrainingRecord {
  trainingName: string;
  trainingsSent: number;
  completed: boolean;
  overdue?: number; // days
  score: number;
}

export interface SecurityAwarenessTrainingUserRecord {
  total: number;
  completed: number;
  avgScore: number;
  userId: string;
  active: boolean;
  trainings: SecurityAwarenessTrainingTrainingRecord[];
}

export interface SecurityAwarenessReportData {
  totalSent: number;
  securityTrainingReport: SecurityAwarenessSummary;
  trainingEngagementSummary: SecurityAwarenessTrainingEngagement;
  userDetails: SecurityAwarenessTrainingUserRecord[];
}

export interface PhishingSimulationReportUserRecord {
  userId: string;
  simulationsSent: number;
  clicked: number;
  noAction: number;
  reported: number;
  phishingTrainingsCompleted: number;
  totalTrainings: number;
  failedPercentage: number;
  phishingSimulations: [
    {
      emailName: string;
      receivedDate: number; // timestamp
      failed: boolean;
      noAction: boolean;
      reported: boolean;
      associatedTrainingCompleted: boolean;
    },
  ];
}

export interface PhishingSimulationReportHistoricRecord {
  month: number;
  year: number;
  reportedPercentage: number;
  failurePercentage: number;
  offenses: number;
  trainingsCompleted: number;
  trainingsCompletedPercentage: number;
  date: number;
}

export interface FailedSimulationReportData {
  failurePercentage: number;
  simulationsSent: number;
  failed: number;
  trainingsCompleted: number;
  simulationName: string;
}

export interface PhishingSimulationReportData {
  totalSent: number;
  phishingSimulationReport: PhishingSimulationSummary;
  historic: PhishingSimulationReportHistoricRecord[];
  simulationEngagement: {
    topPhishedUsers: SecurityAwarenessTrainingEngagementUser[];
    topFailedSimulations: FailedSimulationReportData[];
  };
  userDetails: PhishingSimulationReportUserRecord[];
}

interface ReportsStoreState {
  executiveSummaryReport?: ExecutiveSummaryReportData;
  dnsSummaryReport?: DnsSummaryReportData;
  selectedTimeFrame?: TimeFrame;
  managedServicesSummaryReport?: ManagedServicesReportData;
  securedMessagesSummaryReport?: SecuredMessagesReportData;
  warningOnlyEmailEventsReport?: WarningOnlyEmailEventsReportData;
  warningOnlyEmailEventsMspReport?: WarningOnlyEmailEventsMspReportData;
  phishingSimulationReport: PhishingSimulationReportData;
  securityAwarenessReport: SecurityAwarenessReportData;
  securityAwarenessSearchedUsers: SecurityAwarenessTrainingUserRecord[];
  phishingSimulationSearchedUsers: PhishingSimulationReportUserRecord[];
  warningOnlyEmailEventsUsersPage: number;
  reportUsersSearchLoading: boolean;
  loading: boolean;
  reportVersion: string;
}

const defaultPhishingSimulationReport = {
  totalSent: 0,
  phishingSimulationReport: {
    failed: 0,
    reported: 0,
    noAction: 0,
  },
  simulationEngagement: {
    topPhishedUsers: [],
    topFailedSimulations: [],
    phishingFailuresPerMonth: [],
  },
  historic: [],
  userDetails: [],
};

const defaultSecurityAwarenessReport = {
  totalSent: 0,
  securityTrainingReport: {
    overDue: 0,
    toDo: 0,
    completed: 0,
  },
  trainingEngagementSummary: {
    overdueCourses: [],
    trainingStatus: [],
  },
  userDetails: [],
};

const defaultReportsState: ReportsStoreState = {
  executiveSummaryReport: undefined,
  managedServicesSummaryReport: undefined,
  dnsSummaryReport: undefined,
  selectedTimeFrame: undefined,
  securedMessagesSummaryReport: undefined,
  warningOnlyEmailEventsReport: undefined,
  warningOnlyEmailEventsMspReport: undefined,
  securityAwarenessReport: defaultSecurityAwarenessReport,
  phishingSimulationReport: defaultPhishingSimulationReport,
  securityAwarenessSearchedUsers: [],
  phishingSimulationSearchedUsers: [],
  warningOnlyEmailEventsUsersPage: 0,
  reportUsersSearchLoading: false,
  loading: false,
  reportVersion: "",
};

export const useReportsStore = defineStore(StoreModule.REPORTS, {
  state: (): ReportsStoreState => ({ ...defaultReportsState }),
  getters: {
    domainsPagination(): Pagination {
      return {
        page: this.dnsSummaryReport?.domainsPagination?.page ?? 0,
        pageSize:
          this.dnsSummaryReport?.domainsPagination?.pageSize ?? DNS_REPORT_DEFAULT_PAGE_SIZE,
      };
    },
  },
  actions: {
    async getExecutiveSummaryReport() {
      const { fromTime, toTime } = convertTimeFrameToUnix(this.selectedTimeFrame!);
      const request = {
        ...api.getExecutiveReport(),
        params: {
          fromTime,
          toTime,
        },
      };
      try {
        this.loading = true;
        const { data } = await axiosInstance.request(request);

        this.executiveSummaryReport = data;

        this.reportVersion = getReportVersion();
        this.loading = false;
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },
    async getDnsDomains() {
      const { fromTime, toTime } = convertTimeFrameToUnix(this.selectedTimeFrame!);
      const filters = useFiltersStore();
      const { search, devices, users, status } = filters.filters.dnsReportDomains;
      const pagination = this.domainsPagination;
      const domainsRequest = {
        ...api.getDnsReportDomains(),
        data: {
          search,
          devices,
          users,
          status,
          fromTime,
          toTime,
        },
        params: {
          page: pagination.page,
          pageSize: pagination.pageSize,
          sort: this.dnsSummaryReport?.domainsSorting ?? null,
        },
      };
      try {
        const domainsResponse = await axiosInstance.request(domainsRequest).catch((error) => {
          console.error("Error fetching domains:", error);
          return { data: { items: [], total: 0 } };
        });

        this.dnsSummaryReport!.domainsList = {
          items:
            domainsResponse.data?.items?.map((item: DnsReportDomain) => ({
              ...item,
              id: `${item.domain}-${item.status}`,
            })) ?? [],
          total: domainsResponse.data?.total ?? 0,
        };
        if (!this.dnsSummaryReport?.domainsPagination) {
          this.dnsSummaryReport!.domainsPagination = pagination;
        }

        this.reportVersion = getReportVersion();
      } catch (e) {
        console.error(e);
      }
    },
    async getDnsReport(refresh = true, showLoader = true) {
      const selectedTimeFrame = convertTimeFrameToUTCUnix(this.selectedTimeFrame!);
      const filters = useFiltersStore();
      const { search, devices, users, status } = filters.filters.dnsReportDomains;

      const pagination = this.domainsPagination;
      const request = {
        ...api.getDnsReport(),
        params: {
          ...selectedTimeFrame,
          refresh,
        },
      };
      const domainsRequest = {
        ...api.getDnsReportDomains(),
        data: {
          search,
          devices,
          users,
          status,
          fromTime: selectedTimeFrame.fromTime,
          toTime: selectedTimeFrame.toTime,
        },
        params: {
          page: pagination.page,
          pageSize: pagination.pageSize,
          sort: this.dnsSummaryReport?.domainsSorting ?? null,
        },
      };
      const domainsFiltersRequest = {
        ...api.getDnsFilters(),
      };
      try {
        if (showLoader) {
          this.loading = true;
        }
        const [{ data }, filtersResponse, domainsResponse] = await Promise.all([
          axiosInstance.request(request),
          axiosInstance.request(domainsFiltersRequest).catch((error) => {
            console.error("Error fetching domain filters:", error);
            return { data: { devices: [], users: [] } };
          }),
          axiosInstance.request(domainsRequest).catch((error) => {
            console.error("Error fetching domains:", error);
            return { data: { items: [], total: 0 } };
          }),
        ]);
        data.domainsList = {
          items:
            domainsResponse.data?.items?.map((item: DnsReportDomain) => ({
              ...item,
              id: `${item.domain}-${item.status}`,
            })) ?? [],
          total: domainsResponse.data?.total ?? 0,
        };

        this.dnsSummaryReport = data ?? {};
        this.dnsSummaryReport!.dnsDomainsDevices = filtersResponse.data.devices;
        this.dnsSummaryReport!.dnsDomainsUsers = filtersResponse.data.users;
        if (!this.dnsSummaryReport?.domainsPagination) {
          this.dnsSummaryReport!.domainsPagination = pagination;
        }

        this.reportVersion = getReportVersion();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async dnsDomainsExportCsv() {
      const { fromTime, toTime } = convertTimeFrameToUnix(this.selectedTimeFrame!);
      const filters = useFiltersStore();
      const { search, devices, users, status } = filters.filters.dnsReportDomains;

      const request = {
        ...api.exportDnsDomainsCsv(),
        data: {
          search,
          devices,
          users,
          status,
          fromTime,
          toTime,
        },
      };
      try {
        const { data, headers } = await axiosInstance.request(request);
        const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
        const fileName = "DnsDomainsReport.csv";

        downloadFile(blob, fileName);
      } catch (e) {
        console.error(e);
      }
    },
    async getSecurityAwarenessReport() {
      const selectedTimeFrame = convertTimeFrameToUnix(this.selectedTimeFrame!);
      const request = {
        ...api.getSecurityAwarenessReport({ ...selectedTimeFrame }),
      };
      try {
        this.loading = true;
        const { data } = await axiosInstance.request(request);

        this.securityAwarenessReport = normalizeSecurityAwarenessReportData(data);
        this.reportVersion = getReportVersion();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async searchSecurityAwarenessReportUsers() {
      const filters = useFiltersStore();
      const { search } = filters.filters[FilterContext.SECURITY_AWARENESS_REPORT_USERS];
      const selectedTimeFrame = convertTimeFrameToUnix(this.selectedTimeFrame!);
      const request = {
        ...api.getSecurityAwarenessReportUsers(),
        data: { ...selectedTimeFrame, search },
      };
      try {
        this.reportUsersSearchLoading = true;
        const { data } = await axiosInstance.request(request);

        this.securityAwarenessSearchedUsers = data.users;
      } catch (e) {
        console.error(e);
        this.securityAwarenessSearchedUsers = [];
      } finally {
        this.reportUsersSearchLoading = false;
      }
    },
    async searchPhishingSimulationReportUsers() {
      const filters = useFiltersStore();
      const { search } = filters.filters[FilterContext.PHISHING_SIMULATION_REPORT_USERS];
      const selectedTimeFrame = convertTimeFrameToUnix(this.selectedTimeFrame!);
      const request = {
        ...api.getPhishingSimulationReportUsers(),
        data: { ...selectedTimeFrame, search },
      };
      try {
        this.reportUsersSearchLoading = true;
        const { data } = await axiosInstance.request(request);

        this.phishingSimulationSearchedUsers = data.users;
      } catch (e) {
        console.error(e);
        this.phishingSimulationSearchedUsers = [];
      } finally {
        this.reportUsersSearchLoading = false;
      }
    },
    async getManagedServicesSummaryReport() {
      const selectedTimeFrame = convertTimeFrameToUnix(this.selectedTimeFrame!);
      const request = {
        ...api.getManagedServicesReport(selectedTimeFrame),
      };
      try {
        this.loading = true;
        const { data } = await axiosInstance.request(request);
        this.managedServicesSummaryReport = data;
        this.reportVersion = getReportVersion();
        this.loading = false;
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },
    async getPhishingSimulationReport() {
      const selectedTimeFrame = convertTimeFrameToUnix(this.selectedTimeFrame!);

      const request = {
        ...api.getPhishingSimulationReport({ ...selectedTimeFrame }),
      };
      try {
        this.loading = true;
        const { data } = await axiosInstance.request(request);

        this.phishingSimulationReport = normalizePhishingSimulationReportData(data);
        this.reportVersion = getReportVersion();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async getSecuredMessagesSummaryReport() {
      const selectedTimeFrame = convertTimeFrameToUnix(this.selectedTimeFrame!);
      const request = {
        ...api.getSecuredMessagesReport(selectedTimeFrame),
      };
      try {
        this.loading = true;
        const { data } = await axiosInstance.request(request);
        this.securedMessagesSummaryReport = data;
        this.reportVersion = getReportVersion();
        this.loading = false;
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },
    /**
     * @param tableOnly when true only the users table is refreshed. Paging and searching re-request
     * the whole report because it is the same one endpoint, but the headline and charts come back
     * unchanged, so the page must not be affected by the full-report skeleton loader
     */
    async getWarningOnlyEmailEventsReport(tableOnly = false) {
      const selectedTimeFrame = convertTimeFrameToUTCUnix(this.selectedTimeFrame!);
      const filters = useFiltersStore();
      const { search } = filters.filters[FilterContext.WARNING_ONLY_EMAIL_EVENTS_REPORT_USERS];
      const page = this.warningOnlyEmailEventsUsersPage;
      const { params: timeFrameParams, ...endpoint } =
        api.getWarningOnlyEmailEventsReport(selectedTimeFrame);
      const request = {
        ...endpoint,
        params: {
          ...timeFrameParams,
          page,
          userLimit: WARNING_EMAIL_EVENTS_PAGE_SIZE,
          ...(search ? { search } : {}),
        },
      };
      try {
        if (tableOnly) {
          this.reportUsersSearchLoading = true;
        } else {
          this.loading = true;
        }
        const { data } = await axiosInstance.request(request);
        // top affected user calculate in-place, doing it again on search would show 'top user on page' which is wrong
        const isUnfilteredFirstPage = page === 0 && !search;
        this.warningOnlyEmailEventsReport = {
          ...data,
          topAffectedUsers: isUnfilteredFirstPage
            ? sortBy(data.userDetails, "total").reverse().slice(0, 5)
            : (this.warningOnlyEmailEventsReport?.topAffectedUsers ?? []),
        };
      } catch (e) {
        console.error(e);
      } finally {
        if (tableOnly) {
          this.reportUsersSearchLoading = false;
        } else {
          // Only a full report load re-stamps the version shown in the footer and the PDF.
          this.reportVersion = getReportVersion();
          this.loading = false;
        }
      }
    },
    async setWarningOnlyEmailEventsUsersPage(page: number) {
      this.warningOnlyEmailEventsUsersPage = page;
      await this.getWarningOnlyEmailEventsReport(true);
    },
    async searchWarningOnlyEmailEventsReportUsers() {
      // A new search always restarts at the first page; the result set is a different one
      this.warningOnlyEmailEventsUsersPage = 0;
      await this.getWarningOnlyEmailEventsReport(true);
    },
    async getWarningOnlyEmailEventsMspReport() {
      const selectedTimeFrame = convertTimeFrameToUTCUnix(this.selectedTimeFrame!);
      const request = {
        ...api.getMspWarningOnlyEmailEventsReport(
          selectedTimeFrame,
          MSP_WARNING_EMAIL_EVENTS_WORKSPACE_LIMIT
        ),
      };
      try {
        this.loading = true;
        const { data } = await axiosInstance.request(request);
        this.warningOnlyEmailEventsMspReport = {
          ...data,
          topAffectedWorkspaces: sortBy(data.workspaceDetails, "total").reverse().slice(0, 5),
        };
      } catch (e) {
        console.error(e);
      } finally {
        this.reportVersion = getReportVersion();
        this.loading = false;
      }
    },
    async uploadPdfReport(filename: string, pdfBlob: Blob, reportType: ReportType) {
      const data = new FormData();
      data.append("file", pdfBlob, filename);
      const request = {
        ...api.uploadPdfReport,
        data,
        params: {
          reportType,
        },
      };

      try {
        await axiosInstance.request(request);
      } catch (e) {
        console.error(e);
      }
    },
  },
});

export function getReportVersion(): string {
  return moment().format("YYYYMMDD_HHmmss");
}

function normalizePhishingSimulationReportData(
  rawData: PhishingSimulationReportData
): PhishingSimulationReportData {
  const { simulationEngagement, historic } = rawData;

  const sortedSimulations = simulationEngagement.topFailedSimulations.sort(
    (a, b) => b.failurePercentage - a.failurePercentage
  );

  const sortedUsers = simulationEngagement.topPhishedUsers.sort((a, b) => b.offenses - a.offenses);

  return {
    ...rawData,
    simulationEngagement: {
      topFailedSimulations: sortedSimulations,
      topPhishedUsers: sortedUsers,
    },
    historic: historic
      .map((item) => ({
        ...item,
        date: moment()
          .month(item.month - 1)
          .year(item.year)
          .valueOf(),
      }))
      .sort((a, b) => a.date - b.date),
  };
}

function normalizeSecurityAwarenessReportData(rawData: SecurityAwarenessReportData) {
  const { trainingEngagementSummary } = rawData;

  const sortedTrainings = trainingEngagementSummary.trainingStatus.sort(
    (a, b) => b.percentage - a.percentage
  );

  const sortedUsers = trainingEngagementSummary.overdueCourses
    .sort((a, b) => b.offenses - a.offenses)
    .slice(0, 15);

  return {
    ...rawData,
    trainingEngagementSummary: {
      trainingStatus: sortedTrainings,
      overdueCourses: sortedUsers,
    },
  };
}
